import { OrderHoldPanel, type OrderHoldTarget } from '../components/orders/OrderHoldPanel';
import { OrderQualificationResponses } from '../components/orders/OrderQualificationResponses';
import React, { useState, useEffect } from 'react';
import { useServerList } from '../hooks/useServerList';
import { normalizePagination } from '../lib/pagination';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import {
  ShoppingCart,
  Search,
  User,
  Clock,
  CheckCircle,
  XCircle,
  X,
  Truck,
  Plus,
  Eye,
  FileText,
  AlertCircle,
  RefreshCw,
  DollarSign,
  MessageSquare,
  ExternalLink,
  Pill
} from 'lucide-react';
import { OrderStatus, Order, Patient } from '@bloom/shared';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { paymentApi } from '../lib/paymentApi';
import { useCartStore } from '../store/useCartStore';
import { ShoppingCartComponent } from '../components/cart/ShoppingCart';
import { QRCodeComponent } from '../components/QRCode';
import { OrderPharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { itemsWithPeptide } from '../lib/pharmacyRouting';
import { useProductPeptideMap } from '../hooks/useProductPeptideMap';
import { formatShortDate, formatDateTime } from '../utils/dateUtils';

interface OrderWithPatient extends Omit<Order, 'patientId' | 'invoiceId'> {
  patientId: Patient;
  pharmacyOrderId?: string;
  pharmacySubmissionStatus?: 'not_submitted' | 'partially_submitted' | 'fully_submitted' | 'failed';
  invoiceId?: string | { _id: string; invoiceNumber?: string; status?: string };
}

const getInvoiceData = (invoiceId?: string | { _id: string; invoiceNumber?: string; status?: string }) => {
  if (!invoiceId || typeof invoiceId === 'string') return null;
  return invoiceId;
};

interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  submittedOrders: number;
  deliveredOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
}

const OrderStatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const getStatusConfig = (status: OrderStatus) => {
    switch (status) {
      case 'draft':
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: FileText };
      case 'awaiting_payment':
        return { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock };
      case 'voided':
        return { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle };
      case 'submitted':
        return { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: FileText };
      case 'processing':
        return { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: RefreshCw };
      case 'shipped':
        return { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Truck };
      case 'delivered':
        return { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle };
      case 'cancelled':
        return { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle };
      case 'failed':
        return { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle };
      default:
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertCircle };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3 mr-1" />
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const PaymentStatusBadge: React.FC<{ invoiceStatus?: string }> = ({ invoiceStatus }) => {
  const getConfig = () => {
    switch (invoiceStatus) {
      case 'paid':
        return { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Paid' };
      case 'overdue':
        return { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle, label: 'Overdue' };
      case 'sent':
      case 'unpaid':
        return { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock, label: 'Unpaid' };
      case 'partial':
        return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: DollarSign, label: 'Partial' };
      case 'voided':
        return { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle, label: 'Voided' };
      case 'refunded':
        return { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: DollarSign, label: 'Refunded' };
      default:
        return { color: 'bg-gray-100 text-gray-500 border-gray-200', icon: DollarSign, label: 'N/A' };
    }
  };

  const config = getConfig();
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const OrderCard: React.FC<{
  order: OrderWithPatient;
  onViewDetails: (order: OrderWithPatient) => void;
  onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
  peptideMap?: Record<string, boolean>;
}> = ({ order, onViewDetails, onStatusChange, peptideMap = {} }) => {
  const [isUpdating, setIsUpdating] = useState(false);


  const handleCancel = async () => {
    if (!['draft', 'submitted'].includes(order.status)) return;

    const confirmed = window.confirm('Are you sure you want to cancel this order?');
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      await apiClient.cancelOrder(order._id);
      onStatusChange(order._id, 'cancelled');
    } catch (error) {
      console.error('Failed to cancel order:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWriteScript = async () => {
    if (order.status !== 'draft') return;

    const isPatientPay = order.paymentDetails?.method === 'patient_pay';
    const confirmMessage = isPatientPay
      ? 'Are you sure you want to write this script? A payment link will be sent to the patient.'
      : 'Are you sure you want to finalize this order? An invoice will be generated for payment.';

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      const response = await apiClient.writeScript(order._id);
      if (response.success) {
        onStatusChange(order._id, 'awaiting_payment');
        const successMessage = isPatientPay
          ? response.data?.paymentLinkSent
            ? 'Script written successfully! Payment link sent to patient.'
            : 'Script written successfully! Payment link ready for patient.'
          : 'Script written successfully! Invoice has been created.';
        alert(successMessage);
      } else {
        alert(response.error || 'Failed to write script');
      }
    } catch (error) {
      console.error('Failed to write script:', error);
      alert('Failed to write script. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">#{order.orderNumber}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {formatShortDate(order.createdAt)} • {order.items.length} item{order.items.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <PaymentStatusBadge invoiceStatus={getInvoiceData(order.invoiceId)?.status} />
          <OrderStatusBadge status={order.status} />
          <OrderPharmacyBadge items={itemsWithPeptide(order.items, peptideMap)} size="sm" />
        </div>
      </div>

      <div className="mb-4 space-y-2">
        <div className="flex items-center text-sm text-gray-600">
          <User className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{order.patientId.firstName} {order.patientId.lastName}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="font-semibold">${order.totalAmount.toFixed(2)}</span>
        </div>
        {order.trackingInfo?.trackingNumber && (
          <div className="flex items-center text-sm">
            <Truck className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
            {order.trackingInfo.trackingUrl ? (
              <a
                href={order.trackingInfo.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
              >
                Track: ...{order.trackingInfo.trackingNumber.slice(-6)}
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <span className="text-gray-600">Tracking: ...{order.trackingInfo.trackingNumber.slice(-6)}</span>
            )}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
          <button
            onClick={() => onViewDetails(order)}
            className="flex items-center justify-center lg:justify-start text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 hover:bg-blue-100 px-4 py-2 lg:px-0 lg:py-0 lg:bg-transparent rounded-lg lg:rounded-none transition-colors"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </button>
          
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center space-y-2 lg:space-y-0 lg:space-x-2">
            {order.status === 'draft' && (
              <button
                onClick={handleWriteScript}
                disabled={isUpdating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-3 lg:px-3 lg:py-1 rounded-lg lg:rounded-md text-sm font-medium transition-colors min-h-[44px] lg:min-h-0"
              >
                {isUpdating ? 'Writing...' : 'Write Script'}
              </button>
            )}

            {['draft', 'submitted'].includes(order.status) && (
              <button
                onClick={handleCancel}
                disabled={isUpdating}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-4 py-3 lg:px-3 lg:py-1 rounded-lg lg:rounded-md text-sm font-medium transition-colors min-h-[44px] lg:min-h-0"
              >
                {isUpdating ? 'Cancelling...' : 'Cancel'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* QR Code for Payment - Mobile */}
      {order.status === 'awaiting_payment' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-center">
            <QRCodeComponent
              value={`${window.location.origin}/payment/${order._id}`}
              label={`Order ${order.orderNumber} - $${order.totalAmount.toFixed(2)}`}
              size={120}
              showCopyButton={true}
              className="bg-gray-50"
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Show this QR code to patient for mobile payment
          </p>
        </div>
      )}
    </div>
  );
};

const OrderTableRow: React.FC<{
  order: OrderWithPatient;
  onViewDetails: (order: OrderWithPatient) => void;
  onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
  peptideMap?: Record<string, boolean>;
}> = ({ order, onViewDetails, onStatusChange, peptideMap = {} }) => {
  const [isUpdating, setIsUpdating] = useState(false);


  const handleCancel = async () => {
    if (!['draft', 'submitted'].includes(order.status)) return;

    const confirmed = window.confirm('Are you sure you want to cancel this order?');
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      await apiClient.cancelOrder(order._id);
      onStatusChange(order._id, 'cancelled');
    } catch (error) {
      console.error('Failed to cancel order:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWriteScript = async () => {
    if (order.status !== 'draft') return;

    const isPatientPay = order.paymentDetails?.method === 'patient_pay';
    const confirmMessage = isPatientPay
      ? 'Are you sure you want to write this script? A payment link will be sent to the patient.'
      : 'Are you sure you want to finalize this order? An invoice will be generated for payment.';

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      const response = await apiClient.writeScript(order._id);
      if (response.success) {
        onStatusChange(order._id, 'awaiting_payment');
        const successMessage = isPatientPay
          ? response.data?.paymentLinkSent
            ? 'Script written successfully! Payment link sent to patient.'
            : 'Script written successfully! Payment link ready for patient.'
          : 'Script written successfully! Invoice has been created.';
        alert(successMessage);
      } else {
        alert(response.error || 'Failed to write script');
      }
    } catch (error) {
      console.error('Failed to write script:', error);
      alert('Failed to write script. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <tr>
      {/* Order Details */}
      <td className="cell-primary">
        <div className="cell-stack">
          <span className="cell-stack-value">#{order.orderNumber}</span>
          <span className="cell-stack-label">
            {formatShortDate(order.createdAt)}
          </span>
          <span className="cell-stack-label">
            {order.items.length} item{order.items.length !== 1 ? 's' : ''}
          </span>
        </div>
      </td>

      {/* Patient */}
      <td className="cell-secondary">
        <div className="cell-inline">
          <User className="cell-inline-icon" />
          <span>{order.patientId.firstName} {order.patientId.lastName}</span>
        </div>
      </td>

      {/* Medications */}
      <td>
        <div className="cell-truncate">
          {order.items.map((item, index) => (
            <div key={index} className="flex items-center justify-between mb-1 last:mb-0">
              <span className="truncate text-sm">{item.productName}</span>
              <span className="cell-muted ml-2 flex-shrink-0">×{item.quantity}</span>
            </div>
          ))}
        </div>
      </td>

      {/* Payment */}
      <td>
        <PaymentStatusBadge invoiceStatus={getInvoiceData(order.invoiceId)?.status} />
      </td>

      {/* Fulfillment */}
      <td>
        <div className="flex flex-col items-start gap-1">
          <OrderStatusBadge status={order.status} />
          <OrderPharmacyBadge items={itemsWithPeptide(order.items, peptideMap)} size="sm" />
        </div>
      </td>

      {/* Tracking */}
      <td className="cell-secondary">
        {order.trackingInfo?.trackingNumber ? (
          order.trackingInfo.trackingUrl ? (
            <a
              href={order.trackingInfo.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
            >
              <Truck className="h-3 w-3" />
              <span className="text-xs">...{order.trackingInfo.trackingNumber.slice(-6)}</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="cell-inline">
              <Truck className="cell-inline-icon" />
              <span className="text-xs">...{order.trackingInfo.trackingNumber.slice(-6)}</span>
            </span>
          )
        ) : (
          <span className="cell-muted">—</span>
        )}
      </td>

      {/* Total */}
      <td className="cell-mono">
        <span className="cell-stack-value">${order.totalAmount.toFixed(2)}</span>
      </td>

      {/* Actions */}
      <td className="cell-actions">
        <div className="table-actions flex-wrap">
          <button
            onClick={() => onViewDetails(order)}
            className="table-action-btn table-action-btn-primary"
          >
            <Eye className="h-3 w-3 mr-1" />
            <span className="hidden xl:inline">Details</span>
          </button>
          
          {order.status === 'awaiting_payment' && order.paymentDetails?.method === 'patient_pay' && (
            <>
              <button
                onClick={() => {
                  // Open payment link in new tab
                  const paymentLink = `${window.location.origin}/payment/${order._id}`;
                  window.open(paymentLink, '_blank');
                }}
                className="table-action-btn table-action-btn-primary"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                <span className="hidden xl:inline">Open</span>
              </button>
              <button
                onClick={async () => {
                  // Manually check payment status for this order
                  try {
                    const response = await apiClient.getOrderPaymentStatus(order._id);
                    if (response.success && response.data) {
                      const { paymentStatus, canSubmitToPharmacy } = response.data;
                      
                      // If payment is completed and order can be submitted, update status to draft
                      if (paymentStatus === 'completed' && canSubmitToPharmacy) {
                        console.log(`💰 Payment completed for order ${order.orderNumber} - updating status to draft`);
                        onStatusChange(order._id, 'draft');
                      }
                      // If payment failed, we could add specific handling here
                      else if (paymentStatus === 'failed') {
                        console.log(`❌ Payment failed for order ${order.orderNumber}`);
                        // Could show a notification or update UI accordingly
                      }
                    }
                  } catch (error) {
                    console.error(`Failed to check payment status for order ${order._id}:`, error);
                  }
                }}
                className="table-action-btn table-action-btn-purple"
                title="Refresh payment status"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                <span className="hidden xl:inline">Refresh</span>
              </button>
              <button
                onClick={() => {
                  // Open QR code modal
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50';
                  modal.innerHTML = `
                    <div class="bg-white rounded-lg p-6 max-w-sm w-full">
                      <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-semibold">Payment QR Code</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                      <div id="qr-container-${order._id}" class="flex justify-center mb-4"></div>
                      <p class="text-sm text-gray-600 text-center">Show this QR code to patient for mobile payment</p>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  
                  // Render QR code using React
                  import('react-dom/client').then(({ createRoot }) => {
                    const container = document.getElementById(`qr-container-${order._id}`);
                    if (container) {
                      const root = createRoot(container);
                      root.render(React.createElement(QRCodeComponent, {
                        value: `${window.location.origin}/payment/${order._id}`,
                        label: `Order ${order.orderNumber} - $${order.totalAmount.toFixed(2)}`,
                        size: 200,
                        showCopyButton: true
                      }));
                    }
                  });
                }}
                className="table-action-btn table-action-btn-purple"
              >
                <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M12 12h-2m4 0h2m-6 0v4m8-12h2m-6 0v-1m6 1v3m0 0h.01" />
                </svg>
                <span className="hidden xl:inline">QR</span>
              </button>
              <button
                onClick={async () => {
                  const patientPhone = order.patientId?.phone;
                  if (!patientPhone) {
                    alert('Patient does not have a phone number on file');
                    return;
                  }
                  try {
                    const response = await paymentApi.sendPaymentLinkSms(order._id, patientPhone);
                    if (response.success) {
                      alert('Payment link sent via SMS!');
                    } else {
                      alert(`Failed to send SMS: ${response.error || 'Unknown error'}`);
                    }
                  } catch (error) {
                    alert('Failed to send SMS. Please try again.');
                  }
                }}
                className="table-action-btn table-action-btn-success"
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                <span className="hidden xl:inline">SMS</span>
              </button>
            </>
          )}

          {order.status === 'draft' && (
            <button
              onClick={handleWriteScript}
              disabled={isUpdating}
              className="table-action-btn table-action-btn-primary"
            >
              {isUpdating ? '...' : 'Write Script'}
            </button>
          )}

          {['draft', 'submitted'].includes(order.status) && (
            <button
              onClick={handleCancel}
              disabled={isUpdating}
              className="table-action-btn table-action-btn-danger"
            >
              {isUpdating ? '...' : 'Cancel'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// The outside script a pharmacy account transferred to us for this order. The
// prescriber shown here — not the pharmacy — is who the fulfilling pharmacy
// receives as the prescriber. The scanned document is fetched on demand as a
// short-lived presigned link rather than stored on the order.
const TransferredRxSummary: React.FC<{
  orderId: string;
  transfer: {
    source: 'pdf' | 'manual';
    prescriber: { firstName: string; lastName: string; npiNumber: string; licenseState?: string };
    documentFileName?: string;
    documentS3Key?: string;
    notes?: string;
  };
}> = ({ orderId, transfer }) => {
  const [loadingDoc, setLoadingDoc] = useState(false);

  const openDocument = async () => {
    setLoadingDoc(true);
    try {
      const res: any = await apiClient.get(`/orders/${orderId}/rx-transfer-document`);
      if (res.success && res.data?.downloadUrl) {
        window.open(res.data.downloadUrl, '_blank', 'noopener');
      } else {
        alert(res.error || 'Could not open the prescription document.');
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Could not open the prescription document.');
    } finally {
      setLoadingDoc(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-2">Transferred Prescription</h3>
      <div className="bg-gray-50 rounded-lg p-4 space-y-1">
        <p className="font-medium text-gray-900">
          {transfer.prescriber.firstName} {transfer.prescriber.lastName}
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">NPI:</span> {transfer.prescriber.npiNumber}
          {transfer.prescriber.licenseState ? ` · ${transfer.prescriber.licenseState}` : ''}
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Received:</span>{' '}
          {transfer.source === 'pdf' ? 'Uploaded script' : 'Entered manually'}
        </p>
        {transfer.notes && <p className="text-sm text-gray-600">{transfer.notes}</p>}
        {transfer.documentS3Key && (
          <button
            type="button"
            onClick={openDocument}
            disabled={loadingDoc}
            className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            {loadingDoc ? 'Opening…' : `View ${transfer.documentFileName || 'document'}`}
          </button>
        )}
      </div>
    </div>
  );
};

const OrderDetailModal: React.FC<{
  order: OrderWithPatient | null;
  onClose: () => void;
  onOrderChanged: (updated?: { hold?: unknown; lastPharmacyStatus?: string }) => void;
  peptideMap?: Record<string, boolean>;
}> = ({ order, onClose, onOrderChanged, peptideMap = {} }) => {
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Order #{order.orderNumber}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <OrderHoldPanel order={order as unknown as OrderHoldTarget} isAdmin={false} onChanged={onOrderChanged} />
<OrderQualificationResponses responses={order.qualificationResponses} assessments={order.programAssessments} />
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Payment</h3>
              <PaymentStatusBadge invoiceStatus={getInvoiceData(order.invoiceId)?.status} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Fulfillment</h3>
              <div className="flex flex-col items-start gap-1.5">
                <OrderStatusBadge status={order.status} />
                <OrderPharmacyBadge items={itemsWithPeptide(order.items, peptideMap)} size="sm" />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Order Total</h3>
              <div className="space-y-1">
                {/* Break the total out whenever it is more than the items */}
                {(order.shippingAmount ?? 0) > 0 || (order.telemedNetworkFee ?? 0) > 0 ? (
                  <>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal:</span>
                      <span>${(order.subtotal ?? (order.totalAmount - (order.shippingAmount ?? 0) - (order.telemedNetworkFee ?? 0))).toFixed(2)}</span>
                    </div>
                    {(order.shippingAmount ?? 0) > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Shipping:</span>
                        <span>${(order.shippingAmount ?? 0).toFixed(2)}</span>
                      </div>
                    )}
                    {(order.telemedNetworkFee ?? 0) > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Telemed network fee:</span>
                        <span>${(order.telemedNetworkFee ?? 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-semibold text-gray-900 pt-1 border-t border-gray-200">
                      <span>Total:</span>
                      <span>${order.totalAmount.toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-lg font-semibold text-gray-900">${order.totalAmount.toFixed(2)}</p>
                )}
                {/* Clinic-bound orders are consolidated with the rest of that
                    day's deliveries and billed once per box after dispatch, so
                    a $0 shipping line here is expected rather than an error. */}
                {(order as any).shippingDestination === 'clinic' && !(order.shippingAmount) && (
                  <p className="text-xs text-gray-500 pt-1">
                    Shipping billed separately once this ships with the clinic's other orders.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Patient Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Patient Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {(() => {
                console.log('Patient data:', order.patientId); // Debug log
                const patient = order.patientId;
                
                if (patient && typeof patient === 'object' && patient.firstName) {
                  return (
                    <>
                      <p className="font-medium text-gray-900">
                        {patient.firstName} {patient.lastName}
                      </p>
                      {patient.email && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Email:</span> {patient.email}
                        </p>
                      )}
                      {patient.phone && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Phone:</span> {patient.phone}
                        </p>
                      )}
                    </>
                  );
                } else {
                  return (
                    <div className="text-gray-500">
                      <p>Patient information unavailable</p>
                      <div className="text-xs mt-1">
                        <p>Patient type: {typeof patient}</p>
                        <p>Patient data: {JSON.stringify(patient, null, 2)}</p>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          </div>

          {/* Transferred prescription (pharmacy accounts) */}
          {(order as any).prescriptionTransfer && (
            <TransferredRxSummary orderId={order._id} transfer={(order as any).prescriptionTransfer} />
          )}

          {/* Order Items */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.productName}</p>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    {item.selectedVariants && (
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Prescription Details:</span>
                        <div className="mt-1 space-y-1">
                          {(() => {
                            // Handle prescriptionData specifically
                            if (item.selectedVariants.prescriptionData) {
                              try {
                                const prescriptionData = typeof item.selectedVariants.prescriptionData === 'string' 
                                  ? JSON.parse(item.selectedVariants.prescriptionData)
                                  : item.selectedVariants.prescriptionData;
                                
                                return (
                                  <div className="space-y-2">
                                    {prescriptionData.sig && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Sig:</span>
                                        <span className="ml-2 text-gray-900">{prescriptionData.sig}</span>
                                      </div>
                                    )}
                                    {prescriptionData.refills !== undefined && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Refills:</span>
                                        <span className="ml-2 text-gray-900">{prescriptionData.refills}</span>
                                      </div>
                                    )}
                                    {prescriptionData.daysSupply && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Days Supply:</span>
                                        <span className="ml-2 text-gray-900">{prescriptionData.daysSupply}</span>
                                      </div>
                                    )}
                                    {prescriptionData.paymentMethod && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Payment Method:</span>
                                        <span className="ml-2 text-gray-900 capitalize">{prescriptionData.paymentMethod.replace('_', ' ')}</span>
                                      </div>
                                    )}
                                    {prescriptionData.pharmacyNotes && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Pharmacy Notes:</span>
                                        <span className="ml-2 text-gray-900">{prescriptionData.pharmacyNotes}</span>
                                      </div>
                                    )}
                                    {prescriptionData.doctorNotes && (
                                      <div className="flex py-1">
                                        <span className="font-medium min-w-32 text-gray-700">Doctor Notes:</span>
                                        <span className="ml-2 text-gray-900">{prescriptionData.doctorNotes}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              } catch (error) {
                                return (
                                  <div className="text-red-600 text-xs">
                                    <p>Error parsing prescription data:</p>
                                    <div className="mt-1 p-2 bg-red-50 rounded font-mono">
                                      {item.selectedVariants.prescriptionData}
                                    </div>
                                  </div>
                                );
                              }
                            }
                            
                            // Handle other variants
                            return Object.entries(item.selectedVariants)
                              .filter(([key]) => key !== 'prescriptionData')
                              .map(([key, value]) => {
                                const fieldName = key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, str => str.toUpperCase())
                                  .trim();
                                
                                return (
                                  <div key={key} className="flex py-1">
                                    <span className="font-medium min-w-32 text-gray-700">
                                      {fieldName}:
                                    </span>
                                    <span className="ml-2 text-gray-900">{String(value)}</span>
                                  </div>
                                );
                              });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">${item.totalPrice.toFixed(2)}</p>
                    <p className="text-sm text-gray-600">${item.unitPrice.toFixed(2)} each</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Created</h3>
              <p className="text-sm text-gray-900">{formatDateTime(order.createdAt)}</p>
            </div>
            {order.submittedAt && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Submitted</h3>
                <p className="text-sm text-gray-900">{formatDateTime(order.submittedAt)}</p>
              </div>
            )}
          </div>

          {/* Tracking */}
          {(order.trackingInfo?.trackingNumber || order.trackingUrl) && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Truck className="w-4 h-4" />
                Tracking Information
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {order.trackingInfo?.trackingNumber && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-600">Tracking #:</span>
                    <span className="font-mono text-sm text-gray-900">{order.trackingInfo.trackingNumber}</span>
                  </div>
                )}
                {order.trackingInfo?.carrier && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-600">Carrier:</span>
                    <span className="text-sm text-gray-900">{order.trackingInfo.carrier}</span>
                  </div>
                )}
                {(order.trackingInfo?.trackingUrl || order.trackingUrl) && (
                  <a
                    href={order.trackingInfo?.trackingUrl || order.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Track Package
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Pharmacy Submission Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Pill className="w-4 h-4" />
              Pharmacy Status
            </h3>
            <div className={`rounded-lg p-4 border ${
              order.pharmacySubmissionStatus === 'fully_submitted'
                ? 'bg-green-50 border-green-200'
                : order.pharmacySubmissionStatus === 'failed'
                ? 'bg-red-50 border-red-200'
                : order.pharmacySubmissionStatus === 'partially_submitted'
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  order.pharmacySubmissionStatus === 'fully_submitted'
                    ? 'bg-green-100 text-green-800'
                    : order.pharmacySubmissionStatus === 'failed'
                    ? 'bg-red-100 text-red-800'
                    : order.pharmacySubmissionStatus === 'partially_submitted'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {order.pharmacySubmissionStatus
                    ? order.pharmacySubmissionStatus.replace(/_/g, ' ')
                    : 'not submitted'}
                </span>
                {order.pharmacyOrderId && (
                  <span className="text-xs text-gray-500 font-mono">
                    ID: {order.pharmacyOrderId}
                  </span>
                )}
              </div>
              {!order.pharmacyOrderId && order.pharmacySubmissionStatus !== 'fully_submitted' && (
                <p className="text-sm text-gray-500 mt-2">
                  {order.status === 'awaiting_payment'
                    ? 'Order will be submitted to pharmacy after payment is completed.'
                    : order.status === 'draft' || order.status === 'submitted'
                    ? 'Order is being processed for pharmacy submission.'
                    : 'This order has not been submitted to the pharmacy yet.'}
                </p>
              )}
            </div>
          </div>

          {/* Invoice */}
          {getInvoiceData(order.invoiceId) && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Invoice
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {getInvoiceData(order.invoiceId)!.invoiceNumber || 'Invoice'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Status: <PaymentStatusBadge invoiceStatus={getInvoiceData(order.invoiceId)!.status} />
                    </p>
                  </div>
                  <a
                    href={`/invoice-tracking?invoiceId=${getInvoiceData(order.invoiceId)!._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
                  >
                    View Invoice <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const ORDERS_PAGE_SIZE = 10;

/** One page of the physician's patients matching `search`, for the new-order picker. */
const fetchPatientPage = async ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) => {
  const response = await apiClient.getPatients({ query: search || undefined, limit: pageSize, skip: (page - 1) * pageSize });
  if (!response.success) throw new Error(response.error || 'Failed to load patients');
  const items = (response.data || []) as Patient[];
  const p = normalizePagination((response as any).pagination, { page, pageSize, itemCount: items.length });
  return { items, total: p.total, hasMore: p.hasMore };
};

export const Orders: React.FC = () => {
  const user = useUser();
  const { openCart: _openCart } = useCartStore();

  // Search, status and dates all run on the server (GET /orders), over every
  // order; useServerList debounces typing and returns to page 1 on any change.
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const list = useServerList<OrderWithPatient, { status: string; start: string; end: string }>({
    pageSize: ORDERS_PAGE_SIZE,
    filters: { status: statusFilter, start: dateRange.start, end: dateRange.end },
    fetchPage: async ({ page, pageSize, search, filters }) => {
      const response = await apiClient.getOrders({
        status: filters.status || undefined,
        search: search || undefined,
        startDate: filters.start || undefined,
        endDate: filters.end || undefined,
        limit: pageSize,
        skip: (page - 1) * pageSize,
      });
      if (!response.success || !response.data) {
        throw new Error('An unexpected error occurred while fetching orders.');
      }
      const items = (response.data.orders || []) as OrderWithPatient[];
      const p = normalizePagination(response.data.pagination, { page, pageSize, itemCount: items.length });
      return { items, total: p.total, hasMore: p.hasMore };
    },
  });
  const searchTerm = list.search;
  const currentPage = list.page;
  const totalPages = list.totalPages ?? 1;
  // Nothing has answered yet counts as loading, so the empty state never flashes first.
  const loading = list.loading || (list.total === null && !list.error);
  const error = list.error;
  const fetchOrders = list.reload;

  // Local copy so a status change can update a row without a refetch.
  const [orders, setOrders] = useState<OrderWithPatient[]>([]);
  useEffect(() => { setOrders(list.items); }, [list.items]);
  // productId → isPeptide for the loaded orders, so each order can show which
  // pharmacy (Blue Five Labs vs Greenstone Rx) will fill it.
  const peptideMap = useProductPeptideMap(
    orders.flatMap((o) => o.items.map((i) => String(i.productId)))
  );
  const [selectedOrder, setSelectedOrder] = useState<OrderWithPatient | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  
  // Patient selection for new orders
  const [showPatientSelection, setShowPatientSelection] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  // Check payment status for orders awaiting payment
  useEffect(() => {
    const ordersAwaitingPayment = orders.filter(order => order.status === 'awaiting_payment');
    if (ordersAwaitingPayment.length > 0) {
      checkPaymentStatusForOrders(ordersAwaitingPayment);
    }
  }, [orders]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.getOrderStats();
      if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const checkPaymentStatusForOrders = async (ordersAwaitingPayment: OrderWithPatient[]) => {
    // Check payment status for each order awaiting payment
    for (const order of ordersAwaitingPayment) {
      try {
        const response = await apiClient.getOrderPaymentStatus(order._id);
        if (response.success && response.data) {
          const { paymentStatus, canSubmitToPharmacy } = response.data;
          
          // If payment is completed and order can be submitted, update status to draft
          if (paymentStatus === 'completed' && canSubmitToPharmacy) {
            console.log(`💰 Payment completed for order ${order.orderNumber} - updating status to draft`);
            handleOrderStatusChange(order._id, 'draft');
          }
          // If payment failed, we could add specific handling here
          else if (paymentStatus === 'failed') {
            console.log(`❌ Payment failed for order ${order.orderNumber}`);
            // Could show a notification or update UI accordingly
          }
        }
      } catch (error) {
        console.error(`Failed to check payment status for order ${order._id}:`, error);
        // Don't block other orders if one fails
      }
    }
  };

  const handleOrderStatusChange = (orderId: string, newStatus: OrderStatus) => {
    setOrders(orders.map(order => 
      order._id === orderId ? { ...order, status: newStatus } : order
    ));
    fetchStats(); // Refresh stats after status change
  };

  const handleViewDetails = (order: OrderWithPatient) => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const handleNewOrderClick = () => {
    setShowPatientSelection(true);
  };

  const handlePatientSelection = (patient: Patient) => {
    setShowPatientSelection(false);
    // Navigate to patient profile to start order workflow
    window.location.href = `/patients/${patient._id}`;
  };

  if (!user || !['physician', 'physician_readonly'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access order management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Order Management</h1>
          <p className="text-gray-600 mt-1">Track and manage your patient orders</p>
        </div>
        
        {user.role === 'physician' && (
          <button
            onClick={handleNewOrderClick}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Order
          </button>
        )}
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <StatsCard
            title="Total Orders"
            value={stats.totalOrders || 0}
            icon={ShoppingCart}
            color="bg-blue-100 text-blue-600"
          />
          <StatsCard
            title="Pending Orders"
            value={stats.pendingOrders || 0}
            icon={Clock}
            color="bg-yellow-100 text-yellow-600"
          />
          <StatsCard
            title="Submitted Orders"
            value={stats.submittedOrders || 0}
            icon={FileText}
            color="bg-green-100 text-green-600"
          />
          <StatsCard
            title="Total Revenue"
            value={`$${(stats.totalRevenue || 0).toFixed(2)}`}
            icon={DollarSign}
            color="bg-purple-100 text-purple-600"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => list.setSearch(e.target.value)}
                placeholder="Order number or patient name..."
                className="w-full pl-10 pr-4 py-3 lg:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base lg:text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrderStatus | '')}
              className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base lg:text-sm"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="awaiting_payment">Awaiting Payment</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Loading orders...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => fetchOrders()}
            className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter || dateRange.start || dateRange.end
              ? "No orders match your current filters."
              : "You haven't created any orders yet."}
          </p>
          {user.role === 'physician' && (
            <button
              onClick={handleNewOrderClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Create Your First Order
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {orders.map((order) => (
              <OrderCard
                key={order._id}
                order={order}
                onViewDetails={handleViewDetails}
                onStatusChange={handleOrderStatusChange}
                peptideMap={peptideMap}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block data-table-container">
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order Details</th>
                    <th>Patient</th>
                    <th>Medications</th>
                    <th>Payment</th>
                    <th>Fulfillment</th>
                    <th>Tracking</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <OrderTableRow
                      key={order._id}
                      order={order}
                      onViewDetails={handleViewDetails}
                      onStatusChange={handleOrderStatusChange}
                      peptideMap={peptideMap}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-center space-x-2 py-4 px-4 lg:px-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <button
            onClick={() => list.setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 lg:px-3 lg:py-1 rounded-lg lg:rounded-md bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50 min-h-[44px] lg:min-h-0"
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600 px-2">
            Page {currentPage} of {totalPages}{list.total !== null ? ` · ${list.total} orders` : ''}
          </span>
          
          <button
            onClick={() => list.setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 lg:px-3 lg:py-1 rounded-lg lg:rounded-md bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50 min-h-[44px] lg:min-h-0"
          >
            Next
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={handleCloseModal}
        onOrderChanged={(updated) => {
          if (updated) {
            setSelectedOrder(current => current
              ? ({ ...current, hold: updated.hold, lastPharmacyStatus: updated.lastPharmacyStatus } as typeof current)
              : current);
          }
          void fetchOrders();
        }}
        peptideMap={peptideMap}
      />

      {/* Patient Selection Modal */}
      {showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Select Patient for New Order
              </h3>
              <button
                onClick={() => setShowPatientSelection(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 min-h-[24rem]">
              <p className="text-sm text-gray-600 mb-3">
                Search for the patient to create a new order for:
              </p>
              <AsyncSearchSelect<Patient>
                fetchPage={fetchPatientPage}
                getKey={(patient) => patient._id}
                getLabel={(patient) => `${patient.firstName} ${patient.lastName}`}
                getDescription={(patient) => [
                  patient.phone,
                  patient.email || 'No email',
                  patient.status && patient.status !== 'active' ? patient.status : undefined,
                ].filter(Boolean).join(' • ')}
                value={null}
                onChange={(patient) => { if (patient) handlePatientSelection(patient); }}
                placeholder="Search patients by name, phone or email…"
                noun="patients"
                aria-label="Search patients"
              />
              <p className="text-xs text-gray-500 mt-3">
                Patient not listed?{' '}
                <a href="/patients" className="text-blue-600 hover:text-blue-700">Add them on the Patients page</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Cart */}
      <ShoppingCartComponent />
    </div>
  );
}; 